/**
 * Google Analytics 4 — lazy, consent-gated, SPA-aware adapter.
 *
 * Loads `https://www.googletagmanager.com/gtag/js` on-demand exactly once.
 * Manual page_view dispatch on every React Router change (SPA).
 *
 * Consent gate:
 * - Only loads + fires when `localStorage.fg_cookie_consent_v1 === 'all'`.
 * - Until consent is granted, `gtag` is queued in `window.dataLayer` but
 *   the script never loads, so NO network call is made.
 *
 * Future-proof: the same module is reused by the unified `lib/tracking.js`
 * facade. New pixels (Meta, Pinterest, TikTok) follow this same shape.
 */

const MEASUREMENT_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID;
const SCRIPT_ID = "ga4-gtag-script";

let initialized = false;
let scriptInjected = false;

function isConsented() {
  try {
    return localStorage.getItem("fg_cookie_consent_v1") === "all";
  } catch {
    return false;
  }
}

function ensureDataLayer() {
  if (typeof window === "undefined") return null;
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    // Standard Google snippet
    // eslint-disable-next-line prefer-rest-params
    window.gtag = function () { window.dataLayer.push(arguments); };
  }
  return window.dataLayer;
}

function injectScript() {
  if (scriptInjected || typeof document === "undefined") return;
  if (document.getElementById(SCRIPT_ID)) {
    scriptInjected = true;
    return;
  }
  const s = document.createElement("script");
  s.id = SCRIPT_ID;
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(MEASUREMENT_ID)}`;
  document.head.appendChild(s);
  scriptInjected = true;
}

/**
 * Initialize GA4. Idempotent. Will no-op if:
 *   - REACT_APP_GA4_MEASUREMENT_ID is missing
 *   - User has NOT consented to analytics cookies
 */
export function initGA4() {
  if (initialized) return true;
  if (!MEASUREMENT_ID) return false;
  if (!isConsented()) return false;
  if (typeof window === "undefined") return false;

  ensureDataLayer();
  injectScript();

  window.gtag("js", new Date());
  // Send_page_view: false — we control page_view dispatch manually
  // on React Router changes so SPA navigation is properly tracked.
  window.gtag("config", MEASUREMENT_ID, {
    send_page_view: false,
    anonymize_ip: true,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });

  // Default consent state — explicit, GDPR-aware.
  // Marketing/Ads consent remains denied until the user opts in to those
  // specifically (we currently only gate analytics).
  window.gtag("consent", "default", {
    analytics_storage: "granted",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });

  initialized = true;
  return true;
}

/**
 * Fire a GA4 page_view. Called by `RouteAnalytics` on every SPA route change.
 */
export function gaPageView(pathname, search = "", title = "") {
  if (!initialized) return;
  try {
    const page_location = typeof window !== "undefined" ? window.location.href : "";
    window.gtag("event", "page_view", {
      page_path: pathname + (search || ""),
      page_location,
      page_title: title || (typeof document !== "undefined" ? document.title : ""),
    });
  } catch {
    /* never break the app */
  }
}

/**
 * Fire a GA4 custom event. Params will be visible in the GA4 DebugView and
 * "Events" report. GA4 event names are snake_case by convention.
 *
 * Example:
 *   gaEvent('add_to_cart', { item_id: 'p_123', value: 39.99, currency: 'GBP' })
 */
export function gaEvent(name, params = {}) {
  if (!initialized) return;
  if (!name) return;
  try {
    // Strip null/undefined to keep the GA4 payload clean.
    const clean = {};
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== "") clean[k] = v;
    });
    window.gtag("event", name, clean);
  } catch {
    /* swallow */
  }
}

/**
 * Set a user property (e.g. behavior_type). Useful for GA4 audience building.
 */
export function gaSetUserProperty(key, value) {
  if (!initialized || !key) return;
  try {
    window.gtag("set", "user_properties", { [key]: value });
  } catch {
    /* swallow */
  }
}

/**
 * Read GA4's _ga cookie (anonymous client id). Used later for server-side
 * Measurement Protocol calls so server-fired events join the same session.
 */
export function gaClientId() {
  try {
    const m = (document.cookie.match(/(?:^|;\s*)_ga=([^;]+)/) || [])[1];
    if (!m) return null;
    // _ga value is `GA1.<domain depth>.<client_id1>.<client_id2>`; client_id is the last two parts joined by '.'
    const parts = decodeURIComponent(m).split(".");
    if (parts.length >= 4) return `${parts[parts.length - 2]}.${parts[parts.length - 1]}`;
    return null;
  } catch {
    return null;
  }
}

export const ga4 = {
  isReady: () => initialized,
  measurementId: MEASUREMENT_ID,
};
